import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApiClient } from "../service/API";

/**
 * Urgent Requests loader (by ticket) with:
 * - stable apiRef
 * - WS-triggered refresh
 * - throttle to prevent spam
 * - derived: pendingUrgent, activeUrgent, urgentLock
 *
 * NOTE: Uses backend fields: approvalStatus, active, resolvedDatetime
 */
export function useUrgentRequests({ ticketId, wsMessage, enabled = true }) {
  const apiFromHook = useApiClient();
  const apiRef = useRef(apiFromHook);
  useEffect(() => {
    apiRef.current = apiFromHook;
  }, [apiFromHook]);

  const [urgentList, setUrgentList] = useState([]);
  const [loadingUrgents, setLoadingUrgents] = useState(false);

  const lastFetchAtRef = useRef(0);

  const refreshUrgentList = useCallback(
    async ({ force = false } = {}) => {
      if (!enabled) return;
      if (!ticketId) return;

      const now = Date.now();
      if (!force && now - lastFetchAtRef.current < 300) return; // throttle 300ms
      lastFetchAtRef.current = now;

      try {
        setLoadingUrgents(true);
        const res = await apiRef.current.get(`/urgent/by-ticket/${ticketId}`);
        setUrgentList(res.data || []);
      } catch {
        setUrgentList([]);
      } finally {
        setLoadingUrgents(false);
      }
    },
    [ticketId, enabled]
  );

  // Initial load + when ticket changes
  useEffect(() => {
    if (!enabled) return;
    if (!ticketId) return;
    refreshUrgentList({ force: true });
  }, [ticketId, enabled, refreshUrgentList]);

  // WS-triggered refresh
  useEffect(() => {
    if (!enabled) return;
    if (!wsMessage || !ticketId) return;
    if (wsMessage.type !== "REFRESH") return;

    const wsTid = (wsMessage.ticketId ?? "").trim();
    const pageTid = (ticketId ?? "").trim();
    if (wsTid && wsTid !== pageTid) return;

    refreshUrgentList({ force: false });
  }, [wsMessage, ticketId, enabled, refreshUrgentList]);

  // Derived states (match backend model)
  const pendingUrgent = useMemo(() => {
    return (
      urgentList.find((u) =>
        String(u?.approvalStatus || "").toUpperCase().includes("PENDING")
      ) || null
    );
  }, [urgentList]);

  const activeUrgent = useMemo(() => {
    return (
      urgentList.find((u) => {
        const approved =
          String(u?.approvalStatus || "").toUpperCase() === "APPROVED";
        const resolved = Boolean(u?.completedDateTime);
        return approved && !resolved;
      }) || null
    );
  }, [urgentList]);

  const urgentLock = useMemo(() => Boolean(activeUrgent), [activeUrgent]);

  return {
    urgentList,
    loadingUrgents,
    refreshUrgentList,
    pendingUrgent,
    activeUrgent,
    urgentLock,
  };
}